import React from 'react';
import Form from 'react-bootstrap/Form';
import DatePicker from 'react-datepicker';
import { IMaskInput } from 'react-imask';
import { Button } from '../Button/Button';
import { Container } from './styles';

enum FieldType {
    Text = 'text',
    Email = 'email',
    Number = 'number',
    Password = 'password',
    Select = 'select',
    DatePicker = 'datepicker',
    DateTimePicker = 'datetimepicker',
    TextArea = 'textarea',
    CheckBox = 'checkbox',
    Phone = 'tel',
    Mask = 'mask',
    File = 'file'
}

export interface ISelectProps {
    options: any[];
    displayField: string;
    keyField: string;
}

interface IFieldProps {
    label?: string;
    innerLabel?: string;
    value?: any;
    type?: FieldType;
    change?: (value: any) => void;
    marginLeft?: string;
    marginRight?: string;
    marginTop?: string;
    marginBottom?: string;
    width?: string;
    selectProps?: ISelectProps;
    replaceEnterCodeByValue?: string;
    autoFocus?: boolean;
    autoCaptalize?: string;
    marginLeftMobile?: string;
    marginRightMobile?: string;
    marginTopMobile?: string;
    marginBottomMobile?: string;
    widthMobile?: string;
    blur?: () => void;
    maxLength?: number;
    mask?: string;
    textAreaRows?: number;
    numberMin?: number;
    numberMax?: number;
    disabled?: boolean;
    textMuted?: string;
}

const Field: React.FC<IFieldProps> = ({
    label,
    innerLabel,
    value,
    type,
    change,
    marginLeft,
    marginRight,
    marginTop,
    marginBottom,
    width,
    selectProps,
    replaceEnterCodeByValue,
    autoFocus,
    autoCaptalize,
    marginLeftMobile,
    marginRightMobile,
    marginTopMobile,
    marginBottomMobile,
    widthMobile,
    blur,
    maxLength,
    mask,
    textAreaRows,
    numberMin,
    numberMax,
    disabled,
    textMuted
}) => {

    const inputFile = React.useRef<any>(null);
    const inputElement = React.useRef<any>(null);
    React.useEffect(() => {
        if (inputElement.current && autoFocus) {
            inputElement.current.focus();
        }
    }, []);

    const controlRender = () => {
        if (type === FieldType.DatePicker || type === FieldType.DateTimePicker) {
            const DatePickerCustomInput = React.forwardRef((props: any, ref: any) => {
                return (
                    <input type='text' readOnly={true} defaultValue={props.value} onClick={props.onClick} ref={ref} />
                );
            });
            return (
                <DatePicker 
                    selected={value}
                    onChange={(date: Date) => change ? change(date) : console.log('change event not implemented')}
                    customInput={<DatePickerCustomInput />}
                    dateFormat={type === FieldType.DateTimePicker ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy'}
                    showTimeSelect={type === FieldType.DateTimePicker}
                    isClearable
                    onBlur={blur}
                    disabled={disabled}
                />
            );
        }

        if (type === FieldType.Select) {
            const options = selectProps
                ? selectProps.options.map((option, index) => 
                    <option 
                        key={`option-${index}`}
                        value={option[selectProps.keyField]}
                        data-selected={option[selectProps.keyField] === value}
                    >{option[selectProps.displayField]}</option>
                  )
                : null;
            return (
                <Form.Select 
                    defaultValue={value}
                    onChange={(e: any) => change ? change(e.target.value) : console.log('change event not implemented')}
                    ref={inputElement}
                    onBlur={blur}
                    disabled={disabled}
                >
                    {options}
                </Form.Select>
            );
        }

        if (type === FieldType.TextArea) {
            return (
                <Form.Control
                    as='textarea'
                    rows={textAreaRows || 5}
                    defaultValue={value}
                    onKeyUp={(e: any) => keyUpControl(e)}
                    onPaste={(e: any) => pasteControl(e)}
                    ref={inputElement}
                    autoCapitalize={autoCaptalize || 'none'}
                    onBlur={blur}
                    maxLength={maxLength}
                    disabled={disabled}
                />
            );
        }

        if (type === FieldType.CheckBox) {
            return (
                <Form.Check
                    type='checkbox'
                    label={innerLabel}
                    defaultChecked={value}
                    onChange={(e: any) => change ? change(e.target.checked): console.log('change event not implemented')}
                    disabled={disabled}
                />
            );
        }

        if (type == FieldType.Mask) {
            return (
                <IMaskInput
                    defaultValue={value}
                    onKeyUp={(e: any) => keyUpControl(e)}
                    onPaste={(e: any) => pasteControl(e)}
                    autoFocus={autoFocus}
                    onBlur={blur}
                    mask={mask || ''}
                    className='form-control'
                    disabled={disabled}
                />
            );
        }

        if (type == FieldType.File) {
          return (
            <div className='form-control' style={{display:'flex', alignItems:'center', padding:'0px', paddingLeft:'6px'}}>
              <div style={{flex:'1'}}>{value?.name}</div>
              <Button type="secondary" click={openFile} text='Anexar' height='36px' disabled={disabled} />
              <input
                type='file'
                style={{ display: 'none' }}
                ref={inputFile} 
                onChange={changeFile}
              />
            </div>
          )
        }

        return (
            <Form.Control
                type={type || FieldType.Text}
                defaultValue={value}
                onKeyUp={(e: any) => type === FieldType.Number ? null : keyUpControl(e)}
                onPaste={(e: any) => pasteControl(e)}
                ref={inputElement}
                autoCapitalize={autoCaptalize || 'none'}
                onBlur={blur}
                maxLength={maxLength}
                min={type === FieldType.Number ? numberMin : undefined}
                max={type === FieldType.Number ? numberMax : undefined}
                onChange={(e: any) => type === FieldType.Number ? keyUpControl(e) : null}
                disabled={disabled}
            />
        );
    }

    return (
        <Container
            marginLeft={marginLeft} marginRight={marginRight} marginTop={marginTop} marginBottom={marginBottom}
            marginLeftMobile={marginLeftMobile} marginRightMobile={marginRightMobile} marginTopMobile={marginTopMobile} marginBottomMobile={marginBottomMobile}
            width={width} widthMobile={widthMobile}
        >
            <Form.Group>
                {label ? <Form.Label>{label}</Form.Label> : null}
                {controlRender()}
                {textMuted ? <Form.Text muted>{textMuted}</Form.Text> : null}
            </Form.Group>
        </Container>
    );

    function keyUpControl(e: any) {
        if (!change) {
            console.log('change event not implemented');
            return;
        }
        
        let targetValue = e.target.value;
        
        if (e.keyCode == 13 && replaceEnterCodeByValue) {
            targetValue = targetValue + replaceEnterCodeByValue;
        }

        e.target.value = targetValue;

        change(targetValue);
    }

    function pasteControl(e: any) {
        if (!change) {
            console.log('change event not implemented');
            return;
        }
        
        let clipboardData = e.clipboardData.getData('Text');

        if (clipboardData) {
            clipboardData = (clipboardData+'').replaceAll('\n', ' ');
        }

        setTimeout(() => {
            if (!e?.target?.value?.includes(clipboardData) || e?.target?.value == clipboardData) {
                change(clipboardData);
            }
        }, 500);
    }

    function openFile() {
      inputFile.current.click();
    }

    function changeFile(event: any) {
      if (!change) {
        console.log('change event not implemented');
        return;
      }

      event.stopPropagation();
      event.preventDefault();
  
      var file = event.target.files[0];

      change(file || value);
    }

}

export { Field, FieldType };